import React, { memo } from "react"
import PropTypes from "prop-types"
import { Button, Dropdown, Flex, Tooltip } from "antd"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { createRoleMenuItems } from "../utils/rolesAccessHelpers"
import styles from "../index.module.css"

const RoleItem = memo(({ role, isSelected, onRoleClick, menuItems }) => (
  <div className={`${styles.roleItem} ${isSelected ? styles.roleItemActive : ""}`} onClick={() => onRoleClick(role)}>
    <Text className={styles.roleName}>{role.name}</Text>
    <Dropdown menu={menuItems} trigger={["click"]} placement="bottomRight">
      <Button type="text" className={styles.roleMenuButton}>
        <Icon type="feather" name="MoreVertical" width={20} height={20} />
      </Button>
    </Dropdown>
  </div>
))

RoleItem.displayName = "RoleItem"

RoleItem.propTypes = {
  role: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onRoleClick: PropTypes.func.isRequired,
  menuItems: PropTypes.object.isRequired
}

const RolesList = ({ administrativeRoles, experienceRoles, selectedRoleId, onRoleClick, onRoleEdit, onRoleDelete, onAddRole, t }) => {
  const getRoleMenuItems = (role) => {
    const handlers = {
      onEdit: onRoleEdit,
      onDelete: onRoleDelete
    }
    return createRoleMenuItems(role, handlers, t, Icon, styles)
  }

  return (
    <div className={styles.rolesSection}>
      <Text className={styles.sectionTitle}>{t("staff.roles_access.role")}</Text>

      <div className={styles.rolesList}>
        {administrativeRoles?.length > 0 ? (
          administrativeRoles.map((role) => (
            <RoleItem
              key={role.id}
              role={role}
              isSelected={selectedRoleId === role.id}
              onRoleClick={onRoleClick}
              menuItems={getRoleMenuItems(role)}
            />
          ))
        ) : (
          <div className={styles.emptyState}>
            <Text className={styles.emptyStateText}>{t("staff.roles_access.no_administrative_roles")}</Text>
          </div>
        )}
      </div>

      <Flex gap={8} align="center" className={styles.experienceRoleContainer}>
        <Text className={styles.experienceRoleLabel}>{t("staff.roles_access.experience_role")}</Text>
        <Tooltip title={t("staff.roles_access.experience_role_tooltip")}>
          <Icon type="feather" name="Info" height={20} width={20} />
        </Tooltip>
      </Flex>

      <div className={styles.rolesList}>
        {experienceRoles?.length > 0 ? (
          experienceRoles.map((role) => (
            <RoleItem
              key={role.id}
              role={role}
              isSelected={selectedRoleId === role.id}
              onRoleClick={onRoleClick}
              menuItems={getRoleMenuItems(role)}
            />
          ))
        ) : (
          <div className={styles.emptyState}>
            <Text className={styles.emptyStateText}>{t("staff.roles_access.no_experience_roles")}</Text>
          </div>
        )}
      </div>

      <Button type="primary" className={styles.addRoleButton} onClick={onAddRole}>
        {t("staff.add_role")}
      </Button>
    </div>
  )
}

RolesList.propTypes = {
  administrativeRoles: PropTypes.array.isRequired,
  experienceRoles: PropTypes.array.isRequired,
  selectedRoleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRoleClick: PropTypes.func.isRequired,
  onRoleEdit: PropTypes.func.isRequired,
  onRoleDelete: PropTypes.func.isRequired,
  onAddRole: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
}

export default memo(RolesList)
